import { getAuthHeaders } from '../../statistics/api/auth-headers';

/**
 * Комментарии под публикациями наших пабликов: сервер берёт токен по нику и
 * листает /{media}/comments Graph API. Сервер — /api/presentation/comments
 * (Fastify), нужен вход на сайт.
 *
 * Аватарок в ответе нет и не будет: у Instagram Login в `from` приходят только
 * id и ник автора, фотографии чужих профилей Graph API не отдаёт.
 */

export type MediaComment = {
  id: string;
  mediaId: string;
  /** Автор комментария без @. */
  username: string;
  text: string;
  likeCount: number;
  timestamp: string | null;
  /** Ответ на другой комментарий. */
  isReply: boolean;
  /** Комментарий самого паблика под своей публикацией. */
  isOwner: boolean;
  /** Есть только у TikTok: комментарий рисуется карточкой TikTok, а не Instagram. */
  platform?: 'tiktok';
  /** Имя профиля над комментарием — у TikTok оно, а не ник. */
  nickname?: string;
  /** Аватар на CDN TikTok; у Instagram аватарок нет (см. выше). */
  avatarUrl?: string | null;  /** Ниже — только у TikTok, для вида карточки (см. render-comment-card). */
  replyCount?: number;
  emojis?: TikTokEmoji[];
  imageUrl?: string | null;
  stickerUrl?: string | null;
};

/** Фирменное эмодзи TikTok в тексте: подпись «[Лайк]» на месте [start, end). */
export type TikTokEmoji = { start: number; end: number; id: string };

export type MediaCommentsResponse = {
  comments: MediaComment[];
  errors: Array<{ mediaId: string; error: string }>;
  /** Публикации, где комментариев больше, чем сервер успел прочитать. */
  truncated: string[];
};

/** Столько публикаций сервер принимает за раз. */
export const COMMENTS_BATCH_SIZE = 60;

export async function fetchMediaComments(
  items: Array<{ mediaId: string; username: string }>,
): Promise<MediaCommentsResponse> {
  const res = await fetch('/api/presentation/comments', {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Ошибка сервера комментариев: ${res.status}`);
  }
  return (await res.json()) as MediaCommentsResponse;
}

/**
 * Комментарии под роликами TikTok: сервер читает их веб-ручкой tiktok.com —
 * в Display API текстов нет, только счётчик. Ответ того же вида, что у
 * Instagram, `mediaId` — id ролика.
 */
export async function fetchTikTokComments(
  items: Array<{ videoId: string; username: string }>,
): Promise<MediaCommentsResponse> {
  const res = await fetch('/api/presentation/tiktok-comments', {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || `Ошибка сервера комментариев TikTok: ${res.status}`);
  }
  const json = (await res.json()) as MediaCommentsResponse;
  return { ...json, comments: json.comments.map((c) => ({ ...c, platform: 'tiktok' as const })) };
}
